import type { FurnitureCatalogItem } from "./furniture-catalog";

// Outer assembled dimensions checked on the linked Danish retailer pages.
// Only factual references are retained: all meshes/thumbnails are our own
// procedural approximations. Front details, colours and supports are simplified.
const reference = { sourceCheckedAt: "2026-09-04", modelProvenance: "original-procedural" } as const;
const ikea = { ...reference, brand: "IKEA", collection: "IKEA" } as const;
const jysk = { ...reference, brand: "JYSK", collection: "JYSK" } as const;

export const RETAIL_FURNITURE_CATALOG: FurnitureCatalogItem[] = [
  {
    ...ikea, id: "ikea-nordkisa-nightstand-60447677", name: "NORDKISA bedside table", articleNumber: "604.476.77",
    category: "Night tables", shape: "table", width: 0.40, depth: 0.40, height: 0.67,
    color: "#c9a575", accentColor: "#8e6946", upholstery: "Bamboo · drawer and open shelf",
    materials: ["Bamboo", "Clear lacquer", "Fibreboard", "Melamine laminate"],
    table: { top: "rectangle", use: "bedside", shelf: "solid", support: "panels", drawers: 1 },
    sourceUrl: "https://www.ikea.com/dk/da/p/nordkisa-sengebord-bambus-60447677/",
  },
  {
    ...ikea, id: "ikea-malm-dresser-6-60403602", name: "MALM chest of 6 drawers", articleNumber: "604.036.02",
    category: "Dressers", shape: "cabinet", width: 0.80, depth: 0.48, height: 1.23,
    color: "#f1f0eb", accentColor: "#d8d5cc", upholstery: "White · six smooth drawers",
    materials: ["Particleboard", "Acrylic paint", "Fibreboard", "Paper foil"],
    storage: { doors: 0, drawers: 6, base: "plinth", baseHeight: 0.05 },
    sourceUrl: "https://www.ikea.com/dk/da/p/malm-kommode-6-skuffer-hvid-60403602/",
  },
  {
    ...ikea, id: "ikea-mittzon-standing-120-99527772", name: "MITTZON electric sit/stand desk 120×80", articleNumber: "995.277.72",
    category: "Desks", shape: "table", width: 1.20, depth: 0.80, height: 0.75,
    color: "#c6a276", accentColor: "#f0f0eb", upholstery: "Ash veneer / white · electric 62–126 cm",
    materials: ["Ash veneer", "Particleboard", "Steel", "ABS plastic", "Felt"],
    table: { top: "rectangle", use: "desk", heightAdjustable: { min: 0.62, max: 1.26 } },
    sourceUrl: "https://www.ikea.com/dk/da/p/mittzon-skrivebord-haeve-saenke-elektrisk-asketraesfiner-med-sort-bejdse-hvid-s99527772/",
  },
  {
    ...ikea, collection: "IKEA · IVAR modular", id: "ikea-ivar-short-45-30-179", name: "IVAR short section 45×30×179 cm",
    category: "Bookcases", shape: "bookcase", width: 0.45, depth: 0.30, height: 1.785,
    color: "#cba774", accentColor: "#ad8759", upholstery: "Untreated pine · compact derived configuration",
    materials: ["Solid pine", "Galvanized steel"], shelving: { system: "ivar", sections: 1, shelvesPerSection: 4 },
    sourceUrl: "https://www.ikea.com/dk/da/p/ivar-hylde-fyr-10318159/",
  },
  {
    ...ikea, collection: "IKEA · IVAR modular", id: "ikea-ivar-2-134-30-179", name: "IVAR 2 sections 134×30×179 cm",
    category: "Bookcases", shape: "bookcase", width: 1.34, depth: 0.30, height: 1.785,
    color: "#cba774", accentColor: "#ad8759", upholstery: "Untreated pine · two shallow sections",
    materials: ["Solid pine", "Galvanized steel"], shelving: { system: "ivar", sections: 2, shelvesPerSection: 4 },
    sourceUrl: "https://www.ikea.com/dk/da/cat/ivar-system-11703/",
  },
  {
    ...ikea, collection: "IKEA · IVAR modular", id: "ikea-ivar-3-259-30-179", name: "IVAR 3 sections 259×30×179 cm",
    category: "Bookcases", shape: "bookcase", width: 2.59, depth: 0.30, height: 1.785,
    color: "#cba774", accentColor: "#ad8759", upholstery: "Untreated pine · three shallow sections",
    materials: ["Solid pine", "Galvanized steel"], shelving: { system: "ivar", sections: 3, shelvesPerSection: 4 },
    sourceUrl: "https://www.ikea.com/dk/da/cat/ivar-system-11703/",
  },
  {
    ...ikea, id: "ikea-kleppstad-2-80437234", name: "KLEPPSTAD 2-door wardrobe", articleNumber: "804.372.34",
    category: "Wardrobes", shape: "wardrobe", width: 0.79, depth: 0.55, height: 1.76,
    color: "#f3f1eb", accentColor: "#d5d1c8", upholstery: "White · 2 hinged doors",
    materials: ["Particleboard", "Paper foil", "Fibreboard"], storage: { doors: 2 },
    sourceUrl: "https://www.ikea.com/dk/da/p/kleppstad-garderobeskab-med-2-dore-hvid-80437234/",
  },
  {
    ...ikea, id: "ikea-kleppstad-3-00441758", name: "KLEPPSTAD 3-door wardrobe", articleNumber: "004.417.58",
    category: "Wardrobes", shape: "wardrobe", width: 1.17, depth: 0.55, height: 1.76,
    color: "#f3f1eb", accentColor: "#d5d1c8", upholstery: "White · 3 hinged doors",
    materials: ["Particleboard", "Paper foil", "Fibreboard"], storage: { doors: 3 },
    sourceUrl: "https://www.ikea.com/dk/da/p/kleppstad-garderobeskab-med-3-dore-hvid-00441758/",
  },
  {
    ...ikea, id: "ikea-havsta-00529242", name: "HAVSTA cupboard with plinth", articleNumber: "005.292.42",
    category: "Cupboards", shape: "cabinet", width: 0.81, depth: 0.47, height: 0.89,
    color: "#efede6", accentColor: "#d2cec3", upholstery: "White stained pine · 2 panelled doors",
    materials: ["Solid pine", "Fibreboard"], storage: { doors: 2, front: "panel", baseHeight: 0.10 },
    sourceUrl: "https://www.ikea.com/dk/da/p/havsta-skab-med-sokkel-hvid-00529242/",
  },
  {
    ...ikea, id: "ikea-brimnes-cabinet-80300660", name: "BRIMNES 2-door cupboard", articleNumber: "803.006.60",
    category: "Cupboards", shape: "cabinet", width: 0.78, depth: 0.41, height: 0.95,
    color: "#333431", accentColor: "#212320", upholstery: "Black · 2 hinged doors",
    materials: ["Particleboard", "Paper foil", "Fibreboard"], storage: { doors: 2, front: "panel" },
    sourceUrl: "https://www.ikea.com/dk/da/p/brimnes-skab-med-lager-sort-80300660/",
  },
  {
    ...ikea, id: "ikea-lack-118-80449901", name: "LACK coffee table 118×78", articleNumber: "804.499.01",
    category: "Coffee tables", shape: "table", width: 1.18, depth: 0.78, height: 0.45,
    color: "#f1efe8", accentColor: "#e2dfd6", upholstery: "White · lower storage shelf",
    materials: ["Particleboard", "Fibreboard", "Paper filling"], table: { top: "rectangle", shelf: "solid", legStyle: "square" },
    sourceUrl: "https://www.ikea.com/dk/da/p/lack-sofabord-hvid-80449901/",
  },
  {
    ...ikea, id: "ikea-kragsta-20286638", name: "KRAGSTA round coffee table", articleNumber: "202.866.38",
    category: "Coffee tables", shape: "table", width: 0.90, depth: 0.90, height: 0.48,
    color: "#f1efe8", accentColor: "#e0dcd3", upholstery: "White · Ø90 cm",
    materials: ["Fibreboard", "Laminated beech and walnut"], table: { top: "round", legs: 4, legStyle: "round" },
    sourceUrl: "https://www.ikea.com/dk/da/p/kragsta-sofabord-hvid-20286638/",
  },
  {
    ...ikea, id: "ikea-jakobsfors-90500121", name: "JAKOBSFORS round coffee table", articleNumber: "905.001.21",
    category: "Coffee tables", shape: "table", width: 0.80, depth: 0.80, height: 0.42,
    color: "#c5a170", accentColor: "#ab8656", upholstery: "Oak veneer · Ø80 cm · lower shelf",
    materials: ["Oak veneer", "Plywood", "Solid birch"], table: { top: "round", shelf: "solid", legs: 3, legStyle: "round" },
    sourceUrl: "https://www.ikea.com/dk/da/p/jakobsfors-sofabord-egetraesfiner-90500121/",
  },
  {
    ...jysk, id: "jysk-billund-3611113", name: "BILLUND wardrobe with drawers", articleNumber: "3611113",
    category: "Wardrobes", shape: "wardrobe", width: 0.80, depth: 0.51, height: 1.93,
    color: "#f0efe8", accentColor: "#b89970", upholstery: "White / natural oak look · 2 doors, 2 drawers",
    materials: ["Fibreboard", "Melamine", "HDF"], storage: { doors: 2, drawers: 2 },
    sourceUrl: "https://jysk.dk/opbevaring/garderobeskabe/garderobeskab-billund-80x193-hvid-natur-egefarve",
  },
  {
    ...jysk, id: "jysk-vedde-3601087", name: "VEDDE mirrored wardrobe", articleNumber: "3601087",
    category: "Wardrobes", shape: "wardrobe", width: 1.67, depth: 0.53, height: 1.97,
    color: "#766049", accentColor: "#504638", upholstery: "Dark oak look · 3 doors, 1 mirror",
    materials: ["Melamine", "Fibreboard", "MDF", "Mirror glass"], storage: { doors: 3, mirrorDoor: 1 },
    sourceUrl: "https://jysk.dk/opbevaring/garderobeskabe/garderobeskab-vedde-167x197-m-spejl-vild-moerk-egefarve",
  },
  {
    ...jysk, id: "jysk-fandrup-50-3670383", name: "FANDRUP 1-door wardrobe", articleNumber: "3670383",
    category: "Wardrobes", shape: "wardrobe", width: 0.50, depth: 0.50, height: 1.76,
    color: "#f3f1eb", accentColor: "#d7d3ca", upholstery: "White · 1 hinged door · 3 shelves",
    materials: ["HDF", "Melamine", "Fibreboard", "Solid pine"], storage: { doors: 1 },
    sourceUrl: "https://jysk.dk/opbevaring/garderobeskabe/garderobeskab-fandrup-50x176-1-laage-hvid",
  },
  {
    ...jysk, id: "jysk-fandrup-97-3670378", name: "FANDRUP 2-door wardrobe with drawers", articleNumber: "3670378",
    category: "Wardrobes", shape: "wardrobe", width: 0.97, depth: 0.50, height: 1.76,
    color: "#f3f1eb", accentColor: "#d7d3ca", upholstery: "White · 2 hinged doors · 3 drawers",
    materials: ["HDF", "MDF", "Melamine", "Fibreboard", "Solid pine", "Steel"], storage: { doors: 2, drawers: 3 },
    sourceUrl: "https://jysk.dk/opbevaring/garderobeskabe/garderobeskab-fandrup-97x176-2-laager-3-skuffer-hvid",
  },
  {
    ...jysk, id: "jysk-fandrup-145-3670381", name: "FANDRUP 3-door wardrobe with drawers", articleNumber: "3670381",
    category: "Wardrobes", shape: "wardrobe", width: 1.45, depth: 0.50, height: 1.76,
    color: "#f3f1eb", accentColor: "#d7d3ca", upholstery: "White · 3 hinged doors · 3 drawers",
    materials: ["HDF", "MDF", "Melamine", "Fibreboard", "Solid pine", "Steel"], storage: { doors: 3, drawers: 3 },
    sourceUrl: "https://jysk.dk/opbevaring/garderobeskabe/garderobeskab-fandrup-145x176-3-laager-3-skuffer-hvid",
  },
  {
    ...jysk, id: "jysk-skals-3640396", name: "SKALS cupboard with legs", articleNumber: "3640396",
    category: "Cupboards", shape: "cabinet", width: 0.71, depth: 0.35, height: 0.81,
    color: "#f0eee8", accentColor: "#d4d0c7", upholstery: "White · freestanding version with legs (81 cm high)",
    materials: ["MDF", "Fibreboard", "Foil", "ABS plastic"], storage: { doors: 2, base: "legs", baseHeight: 0.10 },
    sourceUrl: "https://jysk.dk/opbevaring/reoler-og-rumdelere/reolmodul-skals-skab-2-laager-hvid-0",
  },
  {
    ...jysk, id: "jysk-saltvig-3601188", name: "SALTVIG rattan-door cupboard", articleNumber: "3601188",
    category: "Cupboards", shape: "cabinet", width: 0.80, depth: 0.35, height: 1.15,
    color: "#c3a272", accentColor: "#a68659", upholstery: "Ash / rattan · 2 doors",
    materials: ["Solid ash", "Ash veneer", "MDF", "Rattan", "Steel"], storage: { doors: 2, front: "rattan", base: "legs", baseHeight: 0.20 },
    sourceUrl: "https://jysk.dk/spisestue/vitrineskabe/skab-saltvig-2-laager-ask",
  },
  {
    ...jysk, id: "jysk-sneslev-3640374", name: "SNESLEV round coffee table", articleNumber: "3640374",
    category: "Coffee tables", shape: "table", width: 0.70, depth: 0.70, height: 0.41,
    color: "#bea077", accentColor: "#30312e", upholstery: "Natural oak look / steel · Ø70 cm · lower shelf",
    materials: ["MDF", "Melamine", "Steel"], table: { top: "round", shelf: "solid", legStyle: "round" },
    sourceUrl: "https://jysk.dk/stue/sofaborde-og-sideborde/sofabord-sneslev-oe70-natur-egefarve",
  },
  {
    ...jysk, id: "jysk-markskel-3681141", name: "MARKSKEL coffee table", articleNumber: "3681141",
    category: "Coffee tables", shape: "table", width: 1.10, depth: 0.60, height: 0.53,
    color: "#bfa178", accentColor: "#edeae1", upholstery: "White / natural oak look · lower shelf",
    materials: ["Fibreboard", "Melamine", "Plastic"], table: { top: "rectangle", shelf: "solid", support: "panels" },
    sourceUrl: "https://jysk.dk/stue/sofaborde-og-sideborde/sofabord-markskel-60x110-hvid-vild-natur-egefarve",
  },
  {
    ...jysk, id: "jysk-lyngvig-3650037", name: "LYNGVIG coffee table with shelf", articleNumber: "3650037",
    category: "Coffee tables", shape: "table", width: 1.10, depth: 0.60, height: 0.45,
    color: "#c3a073", accentColor: "#ad885c", upholstery: "Natural oak · slatted lower shelf",
    materials: ["Solid oak", "Oak veneer", "Fibreboard"], table: { top: "rectangle", shelf: "slatted", legStyle: "round" },
    sourceUrl: "https://jysk.dk/stue/sofaborde-og-sideborde/sofabord-lyngvig-60x110-m-hylde-natur-eg",
  },
  {
    ...jysk, id: "jysk-nordenskov-extended-3620248", name: "NORDENSKOV dining table · 280 cm extended", articleNumber: "3620248",
    category: "Tables", shape: "table", width: 2.80, depth: 0.90, height: 0.75,
    color: "#c7a47a", accentColor: "#9b754f", upholstery: "Natural oak · extended from 200 to 280 cm · seats up to 8",
    materials: ["Oak veneer", "MDF", "Solid birch", "Solid rubberwood", "Steel"],
    table: { top: "rectangle", use: "dining", legs: 4, legStyle: "square", extendable: { closedWidth: 2.00, intermediateWidths: [2.40] } },
    sourceUrl: "https://jysk.dk/spisestue/spiseborde/spisebord-nordenskov-90x200-280-natur-eg",
  },
  {
    ...jysk, id: "jysk-marstrand-extended-3640237", name: "MARSTRAND oval dining table · 280 cm extended", articleNumber: "3640237",
    category: "Tables", shape: "table", width: 2.80, depth: 1.00, height: 0.75,
    color: "#c5a178", accentColor: "#9d7954", upholstery: "Natural oak · oval top · extended from 200 to 280 cm",
    materials: ["Oak veneer", "MDF", "Solid birch", "Solid rubberwood", "Steel"],
    table: { top: "oval", use: "dining", legs: 4, legStyle: "round", extendable: { closedWidth: 2.00, intermediateWidths: [2.40] } },
    sourceUrl: "https://jysk.dk/spisestue/spiseborde/spisebord-marstrand-100x200-280-natur-eg",
  },
  {
    ...jysk, id: "jysk-lyngvig-extended-3620177", name: "LYNGVIG oval dining table · 260 cm extended", articleNumber: "3620177",
    category: "Tables", shape: "table", width: 2.60, depth: 0.95, height: 0.75,
    color: "#c3a073", accentColor: "#99734f", upholstery: "Natural oak · oval top · extended from 180 to 260 cm · seats up to 8",
    materials: ["Oak veneer", "MDF", "Solid oak", "Solid birch", "Steel"],
    table: { top: "oval", use: "dining", legs: 4, legStyle: "round", extendable: { closedWidth: 1.80, intermediateWidths: [2.20] } },
    sourceUrl: "https://jysk.dk/spisestue/spiseborde/spisebord-lyngvig-95x180-260-natur-eg",
  },
  {
    ...jysk, id: "jysk-svaneke-160-3650133", name: "SVANEKE electric sit/stand desk 160×80", articleNumber: "3650133",
    category: "Desks", shape: "table", width: 1.60, depth: 0.80, height: 0.75,
    color: "#b99669", accentColor: "#242725", upholstery: "Natural oak look / black · default preview at 75 cm · adjustable 70–119 cm",
    materials: ["Melamine", "Fibreboard", "Steel", "PVC", "Polypropylene"],
    table: { top: "rectangle", use: "desk", heightAdjustable: { min: 0.70, max: 1.19 } },
    sourceUrl: "https://jysk.dk/kontor/haeve-saenkeborde/haeve-saenkebord-svaneke-80x160-natur-egefarve-sort",
  },
  {
    ...jysk, id: "jysk-lintrup-3630073", name: "LINTRUP desk with 2 drawers", articleNumber: "3630073",
    category: "Desks", shape: "table", width: 1.40, depth: 0.60, height: 0.75,
    color: "#bd966a", accentColor: "#9a734e", upholstery: "Wild natural oak look · 2 drawers",
    materials: ["Fibreboard", "HDF", "Foil", "Aluminium", "Steel"],
    table: { top: "rectangle", use: "desk", legs: 4, legStyle: "square", drawers: 2 },
    sourceUrl: "https://jysk.dk/kontor/skriveborde/skrivebord-lintrup-60x140-2-skuffer-vild-natur-egefarve",
  },
];
